import { getErrorLevel } from '../../hooks'
import stripSsml from './stripSsml'
import { UneeqState, AnyUneeqMessage, Config } from '../../uneeq'

export const uneeqMessageReducer = (
  state: UneeqState,
  message: AnyUneeqMessage,
  config: Config
) => {
  //console.log('Got uneeq message: ', message)
  switch (message.uneeqMessageType) {
    case 'Ready':
      return { ...state, loadingPercentage: 25 }
    case 'AvatarQuestionText':
      try {
        // don't do anything if it's JSON
        // it's a data message, not a real user question
        JSON.parse(message.question)
        return state
      } catch {}

      state.question = message.question
      state.sending = false
      state.inputMode = 'speech'

      if (message.question) {
        state.sending = true
        // if there was a message input is not a problem
        state.spacebarTapped = false
        state.noInput = false
        // dismiss suggestions as soon as the user replies or
        // chooses a question
        state.onScreenInfo.suggestedResponses = undefined
        // reset empty transcript count
        state.emptyTranscriptCount = 0
        // add to transcript
        state.transcript = [
          ...state.transcript,
          {
            message: stripSsml(message.question),
            user: true,
            time: new Date(Date.now())
          }
        ]
      } else {
        // increment empty transcript count
        state.emptyTranscriptCount += 1
        if (state.emptyTranscriptCount >= config.emptyTranscriptThreshold) {
          return {
            ...state,
            noInput: true
          }
        }
      }
      break
    case 'AvatarAvailable':
      return { ...state, unavailable: false, loadingPercentage: 68 }
    case 'DeviceListUpdated':
      const devices = message.devices
      const selectedDevices = { ...state.selectedDevices }

      // set each device type to default if none is selected
      for (const deviceType in devices) {
        if (
          selectedDevices[deviceType] === undefined &&
          devices[deviceType].length > 0
        ) {
          selectedDevices[deviceType] = devices[deviceType][0].deviceId
        }
      }

      return { ...state, devices, selectedDevices, loadingPercentage: 55 }
    case 'SetMicSuccess':
      return {
        ...state,
        selectedDevices: {
          ...state.selectedDevices,
          audioInput: message.deviceId
        },
        loadingPercentage: 33
      }
    case 'SetCameraSuccess':
      return {
        ...state,
        selectedDevices: {
          ...state.selectedDevices,
          videoInput: message.deviceId
        }
      }
    case 'SetSpeakerSuccess':
      return {
        ...state,
        selectedDevices: {
          ...state.selectedDevices,
          audioOutput: message.deviceId
        },
        loadingPercentage: 73
      }
    case 'SessionEnded':
      return { ...state, ready: false, sessionEnded: true }
    case 'DevicePermissionAllowed':
      return { ...state, permissionAllowed: true, loadingPercentage: 29 }
    case 'AvatarAnswer':
      // Empty answer
      if (!message.answerSpeech) {
        return state
      }

      // add to transcript
      const transcript = message.answerSpeech
        ? [
            ...state.transcript,
            {
              message: stripSsml(message.answerSpeech),
              user: false,
              time: new Date()
            }
          ]
        : state.transcript
      return {
        ...state,
        answer: message.answerSpeech,
        transcript,
        avatarSpeaking: true,
        sending: false
      }
    case 'RecordingStarted':
      return {
        ...state,
        recording: true,
        recordStart: Date.now(),
        avatarSpeaking: false,
        inputMode: 'speech'
      }
    case 'RecordingStopped':
      const recordTime = Date.now() - (state?.recordStart || 0)
      const spacebarTapped = recordTime <= config.tapThreshold
      return {
        ...state,
        recording: false,
        sending: !spacebarTapped,
        spacebarTapped
      }
    case 'AvatarTextInputFinished':
      return {
        ...state,
        // move nextSuggestedResponses into suggestedResponses
        onScreenInfo: {
          ...state.onScreenInfo,
          suggestedResponses:
            state.onScreenInfo.nextSuggestedResponses ||
            state.onScreenInfo.suggestedResponses,
          information:
            state.onScreenInfo.nextInformation ||
            state.onScreenInfo.information,
          nextInformation: undefined,
          nextSuggestedResponses: undefined
        },
        avatarSpeaking: false,
        question: ''
      }
    case 'ClientMediaStreamUpdate':
      return {
        ...state,
        loadingPercentage:
          state.loadingPercentage < 85 ? 85 : state.loadingPercentage
      }
    case 'SessionLive':
      if (config.sendFirstUtterance) {
        return {
          ...state,
          sendFirstUtterance: config.sendFirstUtterance,
          ready: true,
          loadingPercentage: 100
        }
      }

      return { ...state, ready: true, loadingPercentage: 100 }
    case 'ClientPerformanceMessage':
      console.log('Got client performance message: ', message.clientPerformance)

      break
      /*
      clientPerformanceStats contains the following values:

      roundTripTime
      The time it takes for a packet to travel through an IP network, from a sending endpoint to a receiving endpoint and back, not including the time to process the packet at its destination.
      
      packetsLost
      Total number of RTP packets lost for this SSRC.
      
      framesDropped
      The total number of video frames dropped prior to decode or dropped because the frame missed its display deadline for this receiver's track.
      
      framesPerSec
      The number of decoded video frames in the last second.
      
      frameWidth
      Represents the width of the last decoded video frame.
      
      frameHeight
      Represents the height of the last decoded video frame.
      
      qualityLimitationReason
      Provides the current reason for limiting the video resolution and "none" if not any.

      */
      const clientPerformanceStats = message.clientPerformance
      if (
        clientPerformanceStats.inboundRoundTripTime &&
        Number(clientPerformanceStats.inboundRoundTripTime.value) > 0.3
      ) {
        console.warn('Slow network connection')
        return {
          ...state,
          clientPerformance: clientPerformanceStats,
          error: {
            errorCode: 'ClientPerformance',
            message: 'Slow connection'
          }
        }
      } else {
        break
      }

    /**
     * Errors
     */
    case 'SessionError':
      console.log('Got error: ', message)
      if (
        message.error.match(
          /^Conversation API Error: Conversation service took longer/
        )
      ) {
        return state
      }
      try {
        const parsedError = JSON.parse(message.error)
        const errorLevel = getErrorLevel(config, parsedError.errorCode)
        if (errorLevel === 'ignore') {
          return { ...state }
        }
        if (parsedError.errorCode === 16001) {
          return {
            ...state,
            error: {
              errorCode: 'SessionError',
              message:
                'Something went wrong with the conversation service. Please try again.'
            },
            sending: false
          }
        }

        // TODO: parse error again e.g. JSON.parse(parsedError).statusCode ===

        return { ...state, error: parsedError, sending: false }
      } catch {
        return {
          ...state,
          error: {
            errorCode: 'SessionError',
            message: message.error
          },
          sending: false
        }
      }

    case 'ErrorEndingSession':
      return {
        ...state,
        error: {
          errorCode: 'ErrorEndingSession',
          message: 'There was an error ending your session'
        }
      }
    case 'ServiceUnavailable':
      return {
        ...state,
        error: {
          errorCode: 'ServiceUnavailable',
          message: message.error.body
            ? message.error.body.message
            : 'There was an error starting the digital human'
        }
      }
    case 'MicActivityError':
      return {
        ...state,
        error: {
          errorCode: 'MicActivityError',
          message: 'Microphone Error'
        }
      }
    case 'DeviceNotFoundError':
      // Your previous device selection is remembered but if that device
      // is not available oninit there will be an error message.
      // The error is not a problem in this case so we ignore it.
      if (!state.ready) return state

      return {
        ...state,
        error: { errorCode: 'DeviceNotFoundError', message: message.msg }
      }
    case 'AvatarUnavailable':
      return {
        ...state,
        unavailable: true
      }
    case 'ConnectionLost':
      return {
        ...state,
        error: {
          errorCode: 'ConnectionLost',
          message: 'Lost Connection',
          sending: false
        }
      }
    case 'Warning':
      return {
        ...state,
        error: { errorCode: 'warning', message: message.msg }
      }
    case 'DeviceError':
      if (message.error.name === 'NotAllowedError') {
        return { ...state, permissionAllowed: false }
      } else {
        return {
          ...state,
          error: { errorCode: 'DeviceError', message: message.error }
        }
      }
    default:
      break
  }
  return state
}
export default uneeqMessageReducer
